const STORAGE_KEY = 'pev-progress-v1';

export function createEmptyProgress() {
  return {
    schemaVersion: 1,
    bestScores: {},
    recentTargets: {},
    updatedAt: null,
  };
}

export function loadProgress() {
  if (typeof window === 'undefined') return createEmptyProgress();
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (parsed?.schemaVersion === 1 && parsed.bestScores && parsed.recentTargets) return parsed;
  } catch {
    // Ignore corrupt browser state.
  }
  return createEmptyProgress();
}

export function saveProgress(state) {
  const next = { ...state, updatedAt: new Date().toISOString() };
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }
  return next;
}

export function scoreKey({ themeId, subtopicId, headwordId, mode }) {
  return [themeId, subtopicId, headwordId || 'subtopic', mode].join('::');
}

export function getBestScore(state, identity) {
  return state.bestScores[scoreKey(identity)] ?? null;
}

export function recordBestScore(state, identity, result) {
  const key = scoreKey(identity);
  const previous = state.bestScores[key];
  const shouldReplace = !previous
    || result.score > previous.score
    || (result.score === previous.score && result.maximum > previous.maximum);
  if (!shouldReplace) return state;
  return {
    ...state,
    bestScores: {
      ...state.bestScores,
      [key]: {
        score: result.score,
        maximum: result.maximum,
        roundType: result.roundType,
        completedAt: new Date().toISOString(),
      },
    },
  };
}

export function setRecentTargets(state, identity, targetIds) {
  const key = scoreKey(identity);
  return {
    ...state,
    recentTargets: {
      ...state.recentTargets,
      [key]: [...new Set(targetIds)].slice(-20),
    },
  };
}

export function getRecentTargets(state, identity) {
  return state.recentTargets[scoreKey(identity)] ?? [];
}

export function aggregateScore(state, themeId = null, mode = null, subtopicId = null) {
  return Object.entries(state.bestScores).reduce(
    (total, [key, value]) => {
      const [keyThemeId, keySubtopicId, , keyMode] = key.split('::');
      if (themeId && keyThemeId !== themeId) return total;
      if (subtopicId && keySubtopicId !== subtopicId) return total;
      if (mode && keyMode !== mode) return total;
      total.score += value.score ?? 0;
      total.maximum += value.maximum ?? 0;
      total.units += 1;
      return total;
    },
    { score: 0, maximum: 0, units: 0 },
  );
}

export function aggregateSubtopicScore(
  state,
  { themeId, subtopicId, mode, totalUnits, maximumPerUnit },
) {
  const expectedUnits = Number.isFinite(totalUnits) ? Math.max(0, totalUnits) : 0;
  const unitMaximum = Number.isFinite(maximumPerUnit) ? Math.max(0, maximumPerUnit) : 0;
  const matchingScores = Object.entries(state.bestScores).filter(([key]) => {
    const [keyThemeId, keySubtopicId, keyHeadwordId, keyMode] = key.split('::');
    return keyThemeId === themeId
      && keySubtopicId === subtopicId
      && keyHeadwordId !== 'subtopic'
      && keyMode === mode;
  });

  return {
    score: matchingScores.reduce(
      (sum, [, value]) => sum + Math.min(value.score ?? 0, unitMaximum),
      0,
    ),
    maximum: expectedUnits * unitMaximum,
    units: matchingScores.length,
  };
}

export function scorePercent(score) {
  if (!score?.maximum) return 0;
  return Math.min(100, Math.round((score.score / score.maximum) * 100));
}

export function clearProgress() {
  if (typeof window !== 'undefined') window.localStorage.removeItem(STORAGE_KEY);
  return createEmptyProgress();
}
